import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, 
  Briefcase, 
  Mail, 
  Phone, 
  MapPin, 
  Globe, 
  Linkedin, 
  Twitter, 
  Instagram, 
  Facebook, 
  Github, 
  Youtube,
  Save,
  QrCode,
  Palette,
  Loader,
  AlertCircle,
  Camera,
  Upload,
  X,
  Eye,
  ArrowLeft
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { getFirestore, doc, setDoc, getDoc } from 'firebase/firestore';
import { uploadPhoto, deletePhoto } from '../../services/firebase';

const IdentityEditor = ({ existingIdentity = null }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateLoading, setTemplateLoading] = useState(true);
  const [qrCode, setQrCode] = useState(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  
  const [identity, setIdentity] = useState({
    name: '',
    title: '',
    photo: '',
    photoUrl: '',
    photoId: '',
    contact: {
      email: '',
      phone: '',
      website: '',
      address: ''
    },
    social: {
      linkedin: '',
      twitter: '',
      instagram: '',
      facebook: '',
      github: '',
      youtube: ''
    },
    about: '',
    template: 'professional',
    customization: {
      primaryColor: '#2563eb',
      fontFamily: 'Inter, sans-serif',
      showLogo: true,
      logoUrl: ''
    }
  });
  
  // Handle going back to the previous page
  const handleGoBack = () => {
    navigate('/digital-identity');
  };
  
  // Handle viewing the identity
  const handleViewIdentity = useCallback(() => {
    if (existingIdentity && existingIdentity.id) {
      navigate(`/digital-identity/${existingIdentity.id}`);
    }
  }, [existingIdentity, navigate]);
  
  // Handle photo upload
  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file size (limit to 1MB)
    if (file.size > 1024 * 1024) {
      setError('Photo size should be less than 1MB');
      return;
    }
    
    // Check file type
    if (!file.type.match('image.*')) {
      setError('Please upload an image file');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setIdentity(prev => ({
        ...prev,
        photo: e.target.result
      }));
    };
    reader.readAsDataURL(file);
  };
  
  // Remove photo
  const handleRemovePhoto = async () => {
    // If there's a photoId, delete the photo from Firebase Storage
    if (identity.photoId && currentUser) {
      try {
        await deletePhoto(currentUser.uid, identity.photoId);
      } catch (error) {
        console.error('Error deleting photo:', error);
        // Continue even if deletion fails
      }
    }
    
    setIdentity(prev => ({
      ...prev,
      photo: '',
      photoUrl: '',
      photoId: ''
    }));
  };

  // Fetch templates on component mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await api.getIdentityTemplates();
        setTemplates(response.templates);
      } catch (error) {
        console.error('Error fetching templates:', error);
        setError('Failed to load templates. Please try again later.');
      } finally {
        setTemplateLoading(false);
      }
    };
    
    fetchTemplates();
    
    // If editing an existing identity, populate the form
    if (existingIdentity) {
      setIdentity(existingIdentity);
    }
  }, [existingIdentity]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle website URL formatting
    if (name === 'contact.website' && value) {
      // If URL starts with www. but doesn't have a protocol, add https://
      if (value.startsWith('www.') && !value.match(/^https?:\/\//)) {
        const formattedValue = `https://${value}`;
        
        // Handle nested properties
        setIdentity(prev => ({
          ...prev,
          contact: {
            ...prev.contact,
            website: formattedValue
          }
        }));
        return;
      }
    }
    
    // Handle nested properties
    if (name.includes('.')) {
      const [section, field] = name.split('.');
      setIdentity(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    } else {
      setIdentity(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handle template selection
  const handleTemplateChange = (templateId) => {
    const selectedTemplate = templates.find(t => t.id === templateId);
    
    if (selectedTemplate) {
      setIdentity(prev => ({
        ...prev,
        template: templateId,
        customization: {
          ...prev.customization,
          primaryColor: selectedTemplate.colors.primary
        }
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    
    if (!currentUser) {
      setError('You must be logged in to create or edit a digital identity.');
      setLoading(false);
      return;
    }
    
    try {
      // Create a copy of the identity to format URLs before submission
      const formattedIdentity = { 
        ...identity,
        userId: currentUser.uid, // Add user ID for Firebase queries
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      // Format website URL if it exists but doesn't have a protocol
      if (formattedIdentity.contact.website && !formattedIdentity.contact.website.match(/^https?:\/\//)) {
        formattedIdentity.contact.website = `https://${formattedIdentity.contact.website}`;
      }
      
      // Format social media URLs if they exist but don't have a protocol
      Object.keys(formattedIdentity.social).forEach(platform => {
        const url = formattedIdentity.social[platform];
        if (url && !url.match(/^https?:\/\//)) {
          formattedIdentity.social[platform] = `https://${url}`;
        }
      });
      
      // Handle photo upload to Firebase Storage
      if (formattedIdentity.photo && formattedIdentity.photo.startsWith('data:')) {
        // Upload the photo to Firebase Storage
        const photoUploadResult = await uploadPhoto(
          currentUser.uid, 
          formattedIdentity.photo,
          formattedIdentity.photoId || Date.now().toString()
        );
        
        if (photoUploadResult.success) {
          // Store the photo URL and ID in the identity object
          formattedIdentity.photoUrl = photoUploadResult.url;
          formattedIdentity.photoId = photoUploadResult.photoId;
          // Remove the base64 data to avoid Firestore size limits
          formattedIdentity.photo = '';
        } else {
          console.error('Error uploading photo:', photoUploadResult.error);
          setError('Failed to upload photo. Please try again with a smaller image.');
          setLoading(false);
          return;
        }
      }
      
      let response;
      let identityId;
      
      // First try to save to API
      try {
        if (existingIdentity) {
          // Update existing identity
          response = await api.updateIdentity(existingIdentity.id, formattedIdentity);
          identityId = existingIdentity.id;
        } else {
          // Create new identity
          response = await api.createIdentity(formattedIdentity);
          identityId = response.identity.id;
          setQrCode(response.qrCode);
          setShowQrModal(true);
        }
        
        // Update local state with API response
        if (response && response.identity) {
          setIdentity(response.identity);
        }
      } catch (apiError) {
        console.warn('API save failed, continuing with Firebase save:', apiError);
        // If API fails, we'll still try to save to Firebase
        // For new identities, generate a unique ID
        if (!existingIdentity) {
          identityId = Date.now().toString();
        }
      }
      
      // Now save to Firebase
      try {
        const db = getFirestore();
        const docRef = doc(db, "digitalIdentities", identityId || existingIdentity?.id);
        
        await setDoc(docRef, {
          ...formattedIdentity,
          id: identityId || existingIdentity?.id
        }, { merge: true });
        
        console.log("Document saved to Firebase with ID:", identityId || existingIdentity?.id);
      } catch (firebaseError) {
        console.error("Error saving to Firebase:", firebaseError);
        throw firebaseError; // Re-throw to be caught by the outer catch
      }
      
      // Show success message
      if (existingIdentity) {
        setSuccessMessage('Digital identity updated successfully! You can now view it or return to the dashboard.');
      }
      
      // Navigate to the identity view page for new identities
      // For existing ones, we stay on the page and show success message
      if (!existingIdentity) {
        navigate(`/digital-identity/${identityId}`);
      }
    } catch (error) {
      console.error('Error saving identity:', error);
      setError(error.response?.data?.message || 'Failed to save identity. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Get template details
  const getTemplateDetails = (templateId) => {
    return templates.find(t => t.id === templateId) || {};
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* Back to Dashboard button */}
      <div className="mb-4">
        <button 
          onClick={handleGoBack}
          className="inline-flex items-center text-gray-600 hover:text-blue-600 transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Digital Identity Hub
        </button>
      </div>
      
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg shadow-lg p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <QrCode className="h-8 w-8 text-white mr-3" />
            <h1 className="text-2xl font-bold text-white">
              {existingIdentity ? 'Edit Digital Identity' : 'Create Digital Identity'}
            </h1>
          </div>
          
          {/* View button for existing identity */}
          {existingIdentity && (
            <button
              onClick={handleViewIdentity}
              className="flex items-center bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-3 py-1.5 rounded-md transition-all duration-200"
            >
              <Eye className="h-4 w-4 mr-1.5" />
              View
            </button>
          )}
        </div>
        <p className="text-white opacity-90">
          Create a professional digital business card and personal landing page that you can share with anyone via QR code or link.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-md mb-6 flex items-start">
          <AlertCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}
      
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-600 p-4 rounded-md mb-6 flex items-start">
          <div className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0 bg-green-100 rounded-full flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-green-600" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="flex-1">
            <p>{successMessage}</p>
            <div className="mt-2 flex space-x-3">
              <button 
                onClick={handleViewIdentity}
                className="text-green-700 hover:text-green-800 text-sm font-medium flex items-center"
              >
                <Eye className="h-3.5 w-3.5 mr-1" />
                View Identity
              </button>
              <button 
                onClick={handleGoBack}
                className="text-green-700 hover:text-green-800 text-sm font-medium flex items-center"
              >
                <ArrowLeft className="h-3.5 w-3.5 mr-1" />
                Back to Hub
              </button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Personal Information Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-800 flex items-center">
            <User className="h-5 w-5 mr-2 text-blue-600" />
            Personal Information
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Profile Photo
              </label>
              <div className="flex flex-col items-center">
                {identity.photo ? (
                  <div className="relative mb-3">
                    <img 
                      src={identity.photo} 
                      alt="Profile" 
                      className="w-32 h-32 rounded-full object-cover border-2 border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={handleRemovePhoto}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                      title="Remove photo"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="w-32 h-32 rounded-full bg-gray-100 flex items-center justify-center mb-3 border-2 border-dashed border-gray-300">
                    <Camera className="h-10 w-10 text-gray-400" />
                  </div>
                )}
                
                <label className="cursor-pointer bg-blue-50 text-blue-600 px-3 py-1.5 rounded-md border border-blue-200 hover:bg-blue-100 transition-colors flex items-center">
                  <Upload className="h-4 w-4 mr-1" />
                  <span className="text-sm">Upload Photo</span>
                  <input 
                    type="file" 
                    accept="image/*" 
                    onChange={handlePhotoUpload} 
                    className="hidden" 
                  />
                </label>
                <p className="text-xs text-gray-500 mt-1">Max size: 1MB</p>
              </div>
            </div>
            
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={identity.name}
                  onChange={handleChange}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                  Professional Title
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={identity.title}
                  onChange={handleChange}
                  placeholder="e.g. Software Engineer, Marketing Director"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>
          
          <div className="mt-4">
            <label htmlFor="about" className="block text-sm font-medium text-gray-700 mb-1">
              About / Bio
            </label>
            <textarea
              id="about"
              name="about"
              value={identity.about}
              onChange={handleChange}
              rows="3"
              placeholder="A brief description about yourself or your professional background"
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        
        {/* Contact Information Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-800 flex items-center">
            <Phone className="h-5 w-5 mr-2 text-blue-600" />
            Contact Information
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="contact.email" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                <Mail className="h-4 w-4 mr-1 text-gray-500" />
                Email
              </label>
              <input
                type="email"
                id="contact.email"
                name="contact.email"
                value={identity.contact.email}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div>
              <label htmlFor="contact.phone" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                <Phone className="h-4 w-4 mr-1 text-gray-500" />
                Phone
              </label>
              <input
                type="tel"
                id="contact.phone"
                name="contact.phone"
                value={identity.contact.phone}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div>
              <label htmlFor="contact.website" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                <Globe className="h-4 w-4 mr-1 text-gray-500" />
                Website
              </label>
              <input
                type="text"
                id="contact.website"
                name="contact.website"
                value={identity.contact.website}
                onChange={handleChange}
                placeholder="www.example.com or https://example.com"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Enter a website URL (optional). URLs starting with www. will be automatically prefixed with https://
              </p>
            </div>
            
            <div>
              <label htmlFor="contact.address" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                <MapPin className="h-4 w-4 mr-1 text-gray-500" />
                Address
              </label>
              <input
                type="text"
                id="contact.address"
                name="contact.address"
                value={identity.contact.address}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>
        
        {/* Social Media Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-800 flex items-center">
            <Globe className="h-5 w-5 mr-2 text-blue-600" />
            Social Media
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="social.linkedin" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                <Linkedin className="h-4 w-4 mr-1 text-gray-500" />
                LinkedIn
              </label>
              <input
                type="url"
                id="social.linkedin"
                name="social.linkedin"
                value={identity.social.linkedin}
                onChange={handleChange}
                placeholder="https://linkedin.com/in/username"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div>
              <label htmlFor="social.twitter" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                <Twitter className="h-4 w-4 mr-1 text-gray-500" />
                Twitter
              </label>
              <input
                type="url"
                id="social.twitter"
                name="social.twitter"
                value={identity.social.twitter}
                onChange={handleChange}
                placeholder="https://twitter.com/username"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div>
              <label htmlFor="social.instagram" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                <Instagram className="h-4 w-4 mr-1 text-gray-500" />
                Instagram
              </label>
              <input
                type="url"
                id="social.instagram"
                name="social.instagram"
                value={identity.social.instagram}
                onChange={handleChange}
                placeholder="https://instagram.com/username"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div>
              <label htmlFor="social.facebook" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                <Facebook className="h-4 w-4 mr-1 text-gray-500" />
                Facebook
              </label>
              <input
                type="url"
                id="social.facebook"
                name="social.facebook"
                value={identity.social.facebook}
                onChange={handleChange}
                placeholder="https://facebook.com/username"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div>
              <label htmlFor="social.github" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                <Github className="h-4 w-4 mr-1 text-gray-500" />
                GitHub
              </label>
              <input
                type="url"
                id="social.github"
                name="social.github"
                value={identity.social.github}
                onChange={handleChange}
                placeholder="https://github.com/username"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div>
              <label htmlFor="social.youtube" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                <Youtube className="h-4 w-4 mr-1 text-gray-500" />
                YouTube
              </label>
              <input
                type="url"
                id="social.youtube"
                name="social.youtube"
                value={identity.social.youtube}
                onChange={handleChange}
                placeholder="https://youtube.com/c/channelname"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>
        
        {/* Design & Template Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-800 flex items-center">
            <Palette className="h-5 w-5 mr-2 text-blue-600" />
            Design & Template
          </h2>
          
          {templateLoading ? (
            <div className="flex justify-center items-center py-8">
              <Loader className="h-8 w-8 text-blue-600 animate-spin" />
            </div>
          ) : (
            <>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select a Template
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {templates.map(template => (
                    <div 
                      key={template.id}
                      className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                        identity.template === template.id 
                          ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500' 
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                      onClick={() => handleTemplateChange(template.id)}
                    >
                      <div 
                        className="h-24 rounded mb-2"
                        style={{ 
                          background: template.colors.background,
                          borderColor: template.colors.primary
                        }}
                      >
                        <div className="h-6 w-full" style={{ backgroundColor: template.colors.primary }}></div>
                      </div>
                      <h3 className="font-medium text-gray-900">{template.name}</h3>
                      <p className="text-xs text-gray-500">{template.description}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="customization.primaryColor" className="block text-sm font-medium text-gray-700 mb-1">
                    Primary Color
                  </label>
                  <div className="flex items-center">
                    <input
                      type="color"
                      id="customization.primaryColor"
                      name="customization.primaryColor"
                      value={identity.customization.primaryColor}
                      onChange={handleChange}
                      className="h-10 w-10 border-0 p-0 mr-2"
                    />
                    <input
                      type="text"
                      value={identity.customization.primaryColor}
                      onChange={handleChange}
                      name="customization.primaryColor"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="customization.logoUrl" className="block text-sm font-medium text-gray-700 mb-1">
                    Logo URL (optional)
                  </label>
                  <input
                    type="url"
                    id="customization.logoUrl"
                    name="customization.logoUrl"
                    value={identity.customization.logoUrl}
                    onChange={handleChange}
                    placeholder="https://example.com/logo.png"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* Actions section with buttons */}
        <div className="flex flex-wrap justify-between items-center gap-4">
          <div className="flex items-center space-x-4">
            <button
              type="button"
              onClick={handleGoBack}
              className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </button>
            
            {existingIdentity && (
              <button
                type="button"
                onClick={handleViewIdentity}
                className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Eye className="h-4 w-4 mr-2" />
                View Identity
              </button>
            )}
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center justify-center px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-md shadow-md hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-300 transform hover:scale-105"
          >
            {loading ? (
              <>
                <Loader className="animate-spin h-5 w-5 mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-5 w-5 mr-2" />
                {existingIdentity ? 'Update Identity' : 'Create Identity'}
              </>
            )}
          </button>
        </div>
      </form>
      
      {/* QR Code Modal */}
      {showQrModal && qrCode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4 text-gray-900">Your Digital Identity is Ready!</h2>
            <p className="text-gray-600 mb-4">
              Your digital identity has been created successfully. You can now share it with others using this QR code.
            </p>
            
            <div className="flex justify-center mb-4">
              <img src={qrCode} alt="QR Code" className="w-64 h-64" />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowQrModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
              <a
                href={qrCode}
                download="digital-identity-qr.png"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Download QR Code
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IdentityEditor;
